import React from 'react';
import { Box, Button, Typography, CircularProgress, useMediaQuery } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import Logo from '../components/common/Logo';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { updatePlatformLogin } from '../utils/userUtils';
import { useTranslation } from 'react-i18next';

const Login: React.FC = () => {
  const { signInWithGoogle, loading, user } = useAuth();
  const navigate = useNavigate();
  const [signingIn, setSigningIn] = React.useState(false);
  const [loginTriggered, setLoginTriggered] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { t } = useTranslation();

  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    try {
      await signInWithGoogle();
      setLoginTriggered(true); // Mark that login was triggered
    } catch (error) {
      console.error('Error signing in:', error);
    } finally {
      setSigningIn(false);
    }
  };

  React.useEffect(() => {
    // Only redirect if user is on /login (or /)
    if (loginTriggered && user && (location.pathname === '/login' || location.pathname === '/')) {
      (async () => {
        await updatePlatformLogin(user.uid, 'video', {
          email: user.email !== null ? user.email : undefined,
          displayName: user.displayName !== null ? user.displayName : undefined,
          photoURL: user.photoURL !== null ? user.photoURL : undefined
        });
        navigate('/dashboard');
      })();
    }
  }, [loginTriggered, user, navigate, location.pathname]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'black',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      <Box sx={{ width: isMobile ? '100%' : 360, maxWidth: '90vw', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Logo maxWidth={360} />
        <Button
          variant="contained"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleSignIn}
          sx={{
            backgroundColor: '#E31C25',
            color: 'white',
            fontWeight: 700,
            fontSize: '1.1rem',
            borderRadius: 2,
            width: isMobile ? '100%' : 320,
            py: 1.5,
            mt: 2,
            mb: 2,
            '&:hover': { backgroundColor: '#B71C1C' },
          }}
          disabled={signingIn || loading}
        >
          {signingIn || loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : t('auth.signInWithGoogle')}
        </Button>
      </Box>
      <Box sx={{ position: 'absolute', bottom: 24, left: 0, right: 0, textAlign: 'center' }}>
        <Typography variant="body2" color="white" sx={{ opacity: 0.7 }}>
          &copy; 2025
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
